import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { Copy, Eye, ImageOff, Pencil, PlusCircle, Trash2 } from 'lucide-react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../config/axios'
import { StandardDataTable, useColumnsState } from '../../../../components/StandardPage'
import {
  buildCompositionColumns,
  compositionScreenKey,
  ProductListItem,
} from './compositionColumns'

const InfoMessage = styled.div`
  padding: 60px 40px;
  text-align: center;
  color: #999;
  font-size: 16px;
  background-color: white;
  border-radius: 8px;
`

const BoardWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #c0c0c0;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ffffff;
    border-radius: 10px;
    border: 2px solid #c0c0c0;
  }
`

const GATEWAY_URL = process.env.REACT_APP_GATEWAY_BASE_URL
const API_BASE_URL = `${GATEWAY_URL}/backend-fashion-orders/fashion-product`

const BOARD_COVER_PALETTE = ['#EDE3B9', '#D9D5EA', '#CBE9DD', '#EFD7D9', '#D0E3F2']
const IMAGE_ATTACHMENT_EXTENSION_REGEX =
  /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif|jfif)$/i

interface Grade {
  id: number
  name: string
  typeId: number
  GraduationType?: {
    id: number
    name: string
  }
}

interface GradeInProduct {
  id: number
  productId: number
  gradeId: number
  Grade: Grade
}

interface Group {
  id: number
  name: string
  tenantId: number
}

interface Family {
  id: number
  name: string
  collectionId: number
  tenantId: number
}

interface ProductResponse {
  id: number
  norm: string
  name: string
  productCode: string
  collectionId: number
  familyId: number
  groupId: number
  createdAt?: string
  updatedAt?: string
  status?: boolean
  Group?: Group
  Family?: Family
  Collection?: {
    id: number
    name: string
    tenantId: number
  }
  Episode?: {
    id: number
    name: string
  }
  GradeInProduct?: GradeInProduct[]
  CoverGeneralFile?: { fileUrl?: string } | null
  coverImageScale?: number | null
  coverImagePosX?: number | null
  coverImagePosY?: number | null
  attachments?: string[]
}

type ProductListItemDraft = ProductListItem & { coverSourcePath: string | null }

const isImageAttachmentUrl = (url: string) => {
  const nameFromUrl = url.split('/').pop() || url
  try {
    return IMAGE_ATTACHMENT_EXTENSION_REGEX.test(decodeURIComponent(nameFromUrl))
  } catch {
    return IMAGE_ATTACHMENT_EXTENSION_REGEX.test(nameFromUrl)
  }
}

const resolveCoverSource = (product: ProductResponse): string | null => {
  const coverFileUrl = product.CoverGeneralFile?.fileUrl
  if (coverFileUrl) return coverFileUrl

  const attachments = product.attachments || []
  const firstImage = attachments.find((url) => isImageAttachmentUrl(url))
  return firstImage || null
}

const getFashionCoverSignedUrl = async (
  filePath: string,
  token: string,
): Promise<string | null> => {
  if (!filePath) return null

  try {
    const response = await axios.get(`${API_BASE_URL}/product-attachments/signed-url`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { filePath },
    })
    return response.data?.data?.url || response.data?.url || null
  } catch {
    return null
  }
}

const BoardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding-right: 10px;
`

const BoardCard = styled.div`
  background: #f7f7f7;
  border: 1px solid #efefef;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const CreateBoardCard = styled.button`
  min-height: 262px;
  border: 2px dashed #d5d5d5;
  border-radius: 16px;
  background: #f1f1f1;
  color: #8a8a8a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;

  span {
    font-size: 16px;
    font-weight: 700;
  }

  &:hover {
    border-color: #bfbfbf;
    background: #ececec;
    color: #767676;
  }
`

const BoardCoverArea = styled.div<{ $background: string }>`
  height: 140px;
  background: ${(props) => props.$background};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
`

const BoardCoverImage = styled.img<{ $scale?: number | null; $posX?: number | null; $posY?: number | null }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: ${(props) => props.$posX ?? 50}% ${(props) => props.$posY ?? 50}%;
  transform: scale(${(props) => props.$scale ?? 1});
`

const BoardCoverFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #7a7a7a;

  span {
    font-size: 12px;
    font-weight: 600;
  }
`

const COVER_PREVIEW_SIZE = 420
const COVER_PREVIEW_PADDING = 8
const COVER_PREVIEW_OFFSET = 24
const COVER_PREVIEW_VIEWPORT_MARGIN = 12

const CoverHoverPreview = styled.div`
  position: fixed;
  z-index: 3000;
  pointer-events: none;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 16px;
  padding: ${COVER_PREVIEW_PADDING}px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);

  img {
    display: block;
    width: ${COVER_PREVIEW_SIZE}px;
    height: ${COVER_PREVIEW_SIZE}px;
    object-fit: contain;
    border-radius: 12px;
    background: #fafafa;
  }
`

const computeCoverPreviewPosition = (mouseX: number, mouseY: number) => {
  const previewTotalSize = COVER_PREVIEW_SIZE + COVER_PREVIEW_PADDING * 2
  let left = mouseX + COVER_PREVIEW_OFFSET
  if (left + previewTotalSize > window.innerWidth - COVER_PREVIEW_VIEWPORT_MARGIN) {
    left = mouseX - COVER_PREVIEW_OFFSET - previewTotalSize
  }
  if (left < COVER_PREVIEW_VIEWPORT_MARGIN) {
    left = COVER_PREVIEW_VIEWPORT_MARGIN
  }
  let top = mouseY - previewTotalSize / 2
  if (top + previewTotalSize > window.innerHeight - COVER_PREVIEW_VIEWPORT_MARGIN) {
    top = window.innerHeight - COVER_PREVIEW_VIEWPORT_MARGIN - previewTotalSize
  }
  if (top < COVER_PREVIEW_VIEWPORT_MARGIN) {
    top = COVER_PREVIEW_VIEWPORT_MARGIN
  }
  return { left, top }
}

const BoardInfo = styled.div`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BoardCode = styled.span`
  color: #8d8d8d;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BoardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #101010;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BoardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
`

const TagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CollectionTagBadge = styled(TagBadge)`
  color: #4a2d7a;
  background: #f3eefb;
`

const EpisodeTagBadge = styled(TagBadge)`
  color: #1e5c3a;
  background: #e8f5ee;
`

const FamilyTagBadge = styled(TagBadge)`
  color: #0f3f59;
  background: #eef6fb;
`

const CompositionTable: React.FC = () => {
  const contextOutlet = useOutletContext<any>()
  const refresh = contextOutlet?.refresh
  const search = contextOutlet?.search
  const setIsLoading = contextOutlet?.setIsLoading
  const viewMode = contextOutlet?.viewMode
  const appliedFilters = contextOutlet?.appliedFilters || []

  const filterColecao = appliedFilters?.find((f: any) => f.key === 'colecao')?.value
  const filterFamilia = appliedFilters?.find((f: any) => f.key === 'familia')?.value
  const filterEpisodio = appliedFilters?.find((f: any) => f.key === 'episodio')?.value

  const navigate = useNavigate()

  const [products, setProducts] = useState<ProductListItem[]>([])
  const [visibleCount, setVisibleCount] = useState<number>(50)
  const [totalProducts, setTotalProducts] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [deleteCandidate, setDeleteCandidate] = useState<{
    id: number
    name: string
  } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)
  const [coverPreview, setCoverPreview] = useState<{
    url: string
    name: string
    left: number
    top: number
  } | null>(null)

  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => buildCompositionColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } = useColumnsState(
    compositionScreenKey,
    columns,
  )

  useEffect(() => {
    setVisibleCount(50)
  }, [filterColecao, filterFamilia, filterEpisodio])

  const fetchData = useCallback(async () => {
    setLoading(true)
    if (setIsLoading) setIsLoading(true)
    setError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/paginated`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: 0,
          size: visibleCount,
          search: search || undefined,
          colecao: filterColecao || undefined,
          familia: filterFamilia || undefined,
          episodio: filterEpisodio || undefined,
          filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
        },
      })

      const data = response.data
      let productsData: ProductResponse[] = []
      if (Array.isArray(data)) {
        productsData = data
        setTotalProducts(null)
      } else if (data && Array.isArray(data.content)) {
        productsData = data.content
        setTotalProducts(typeof data.totalElements === 'number' ? data.totalElements : null)
      }

      const transformedProducts: ProductListItemDraft[] = productsData.map(
        (product) => {
          const collection = product.Collection?.name || '-'
          const episode = product.Episode?.name || '-'
          const family = product.Family?.name || '-'

          const grades = product.GradeInProduct?.map(gp => gp.Grade?.name).join(', ') || '-'
          const groups = product.Group?.name || '-'
          const colorSet = new Set<string>()
          const pics = (product as any).ProductItemColor || []
          for (const pic of pics) {
            const cc = pic.CollectionColor || pic.collectionColor
            if (cc && cc.name) colorSet.add(cc.name)
          }
          const colors = colorSet.size > 0 ? Array.from(colorSet).join(', ') : '-'

          return {
            id: product.id,
            code: product.productCode || product.norm || '-',
            name: product.name || '-',
            collection,
            episode,
            family,
            grades,
            groups,
            colors,
            coverImageUrl: null,
            coverImageScale: typeof product.coverImageScale === 'number' ? product.coverImageScale : null,
            coverImagePosX: typeof product.coverImagePosX === 'number' ? product.coverImagePosX : null,
            coverImagePosY: typeof product.coverImagePosY === 'number' ? product.coverImagePosY : null,
            coverSourcePath: resolveCoverSource(product),
          }
        },
      )

      const productsWithCover: ProductListItem[] =
        viewMode === 'board'
          ? await Promise.all(
              transformedProducts.map(async (item) => {
                const { coverSourcePath, ...rest } = item
                if (!coverSourcePath) {
                  return rest
                }

                const coverImageUrl =
                  (await getFashionCoverSignedUrl(coverSourcePath, token)) ||
                  coverSourcePath

                return {
                  ...rest,
                  coverImageUrl,
                }
              }),
            )
          : transformedProducts.map(({ coverSourcePath: _coverSourcePath, ...rest }) => rest)

      setProducts(productsWithCover)
    } catch (err: unknown) {
      console.error('Erro ao buscar produtos:', err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError('Não autorizado. Por favor, faça login novamente.')
          navigate('/login')
        } else {
          setError(err.response?.data?.message || 'Erro ao carregar os dados.')
        }
      } else {
        setError('Erro desconhecido ao carregar dados.')
      }
    } finally {
      setLoading(false)
      if (setIsLoading) setIsLoading(false)
    }
  }, [visibleCount, search, filterColecao, filterFamilia, filterEpisodio, viewMode, setIsLoading, navigate, appliedFilters])

  useEffect(() => {
    fetchData()
  }, [fetchData, refresh])

  const handleEdit = (productId: number) => {
    setCoverPreview(null)
    navigate(`/panel/composition-product/edit/${productId}`)
  }

  const handleCreateVariation = (productId: number) => {
    setCoverPreview(null)
    navigate(`/panel/composition-product/create?variationFrom=${productId}`)
  }

  const handleCoverPreviewMove =
    (url: string, name: string) => (event: React.MouseEvent<HTMLDivElement>) => {
      const { left, top } = computeCoverPreviewPosition(event.clientX, event.clientY)
      setCoverPreview({ url, name, left, top })
    }

  const handleDelete = (productId: number) => {
    const product = products.find((p) => p.id === productId)
    const productName = product ? product.name : productId

    setDeleteCandidate({ id: productId, name: String(productName) })
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        statusOk: false,
        message: 'Sessão expirada. Por favor, faça login novamente.',
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      setError(null)
      await axios.delete(`${API_BASE_URL}/${deleteCandidate.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      setProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== deleteCandidate.id),
      )

      setFeedback({
        isOpen: true,
        statusOk: true,
        message: 'Produto excluído com sucesso!',
      })
    } catch (err: unknown) {
      console.error(`Erro ao excluir produto ${deleteCandidate.id}:`, err)
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setError(
            'Não autorizado para excluir. Por favor, faça login novamente.',
          )
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
          })
          navigate('/login')
        } else {
          const errorMessage =
            err.response?.data?.message || 'Falha ao excluir o produto.'
          setError(errorMessage)
          setFeedback({
            isOpen: true,
            statusOk: false,
            message: errorMessage,
          })
        }
      } else {
        setError('Erro inesperado ao excluir produto.')
        setFeedback({
          isOpen: true,
          statusOk: false,
          message: 'Erro inesperado ao excluir produto.',
        })
      }
    } finally {
      setLoading(false)
      setShowDeleteConfirm(false)
      setDeleteCandidate(null)
    }
  }

  if (loading && products.length === 0) {
    return <InfoMessage>Carregando produtos...</InfoMessage>
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  if (!loading && products.length === 0) {
    const hasFilters =
      (filterColecao || '').trim() ||
      (filterFamilia || '').trim() ||
      (filterEpisodio || '').trim()

    return (
      <InfoMessage>
        {search || hasFilters
          ? 'Nenhum produto encontrado com os critérios de busca.'
          : 'Nenhum produto cadastrado. Clique em "Cadastrar produto" para começar.'}
      </InfoMessage>
    )
  }

  return (
    <>
      {viewMode === 'table' ? (
        <StandardDataTable
          columns={columns}
          data={products}
          columnsState={columnsState}
          onColumnsChange={handleColumnsChange}
          onRestoreColumns={handleRestoreColumns}
          totalCount={totalProducts}
          onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
          isLoading={loading}
          countLabel='produtos'
          exportFileName='fichas-tecnicas'
          rowActions={(product) => [
            { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleEdit(product.id) },
            { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEdit(product.id) },
            { label: 'Criar Variação', icon: <Copy size={16} />, onClick: () => handleCreateVariation(product.id) },
            { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDelete(product.id) },
          ]}
        />
      ) : (
        <BoardWrapper>
          <BoardGrid>
            <CreateBoardCard onClick={() => navigate('/panel/composition-product/create')}>
              <PlusCircle size={28} />
              <span>Nova Ficha</span>
            </CreateBoardCard>
            {products.map((product: ProductListItem, index: number) => (
              <BoardCard key={product.id}>
                <BoardCoverArea
                  $background={BOARD_COVER_PALETTE[index % BOARD_COVER_PALETTE.length]}
                  onClick={() => handleEdit(product.id)}
                  onMouseEnter={
                    product.coverImageUrl
                      ? handleCoverPreviewMove(product.coverImageUrl, product.name)
                      : undefined
                  }
                  onMouseMove={
                    product.coverImageUrl
                      ? handleCoverPreviewMove(product.coverImageUrl, product.name)
                      : undefined
                  }
                  onMouseLeave={product.coverImageUrl ? () => setCoverPreview(null) : undefined}
                >
                  {product.coverImageUrl ? (
                    <BoardCoverImage
                      src={product.coverImageUrl}
                      alt={product.name}
                      $scale={product.coverImageScale}
                      $posX={product.coverImagePosX}
                      $posY={product.coverImagePosY}
                    />
                  ) : (
                    <BoardCoverFallback>
                      <ImageOff size={34} />
                      <span>Sem capa</span>
                    </BoardCoverFallback>
                  )}
                </BoardCoverArea>

                <BoardInfo>
                  <BoardCode>
                    {`Cod. ${product.code}`}
                  </BoardCode>
                  <BoardTitle title={product.name}>{product.name}</BoardTitle>

                  <BoardFooter>
                    {product.collection !== '-' && (
                      <CollectionTagBadge title={product.collection}>
                        {product.collection}
                      </CollectionTagBadge>
                    )}
                    {product.episode !== '-' && (
                      <EpisodeTagBadge title={product.episode}>
                        {product.episode}
                      </EpisodeTagBadge>
                    )}
                    {product.family !== '-' && (
                      <FamilyTagBadge title={product.family}>
                        {product.family}
                      </FamilyTagBadge>
                    )}
                  </BoardFooter>
                </BoardInfo>
              </BoardCard>
            ))}
          </BoardGrid>

          <LoadMoreButton
            currentCount={products.length}
            totalCount={totalProducts ?? undefined}
            hasMore={typeof totalProducts === 'number' ? products.length < totalProducts : false}
            onLoadMore={() => setVisibleCount((prev) => prev + 50)}
          />

          {coverPreview && (
            <CoverHoverPreview style={{ left: coverPreview.left, top: coverPreview.top }}>
              <img src={coverPreview.url} alt={coverPreview.name} />
            </CoverHoverPreview>
          )}
        </BoardWrapper>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Tem certeza que deseja excluir este produto?"
        message={
          deleteCandidate
            ? `Produto: ${deleteCandidate.name}
              Essa ação não poderá ser desfeita.`
            : 'Essa ação não poderá ser desfeita.'
        }
        actions={[
          {
            label: 'Não, voltar',
            onClick: () => setShowDeleteConfirm(false),
            variant: 'outline',
          },
          {
            label: 'Sim, excluir',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />
    </>
  )
}

export default CompositionTable
